import { useCallback, useEffect, useState } from 'react';
import {
    ConnectionQuality,
    LocalTrack,
    LocalVideoTrack,
    Participant,
    RemoteTrack,
    Track,
    VideoSenderStats,
} from 'livekit-client';
import { selectIsStatisticVisible } from 'state/selectors';
import { VideoInfoProps } from '../types';

type ParticipantProp = {
    type: 'participant';
    entity: Participant;
};

type TrackProp = {
    type: 'track';
    entity: Track;
};

type Props = ParticipantProp | TrackProp;

export const useVideoStatistic = ({ type, entity }: Props): VideoInfoProps => {
    const isStatisticVisible = selectIsStatisticVisible();

    const [videoSize, setVideoSize] = useState<{
        width: number;
        height: number;
    }>();
    const [currentBitrate, setCurrentBitrate] = useState<number>();
    const [stats, setStatsLocal] = useState<VideoSenderStats[]>();
    const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>();

    const onVideoSizeChanged = useCallback((width: number, height: number) => {
        setVideoSize({ width, height });
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            let total = 0;
            let hasActiveVideoTrack = false;

            if (type === 'participant') {
                const participant = entity;
                participant.trackPublications.forEach(async (pub) => {
                    if (pub.track instanceof LocalTrack || pub.track instanceof RemoteTrack) {
                        total += pub.track.currentBitrate;

                        // проверяем наличие активного видео трека
                        if (pub.videoTrack && !pub.isMuted) {
                            hasActiveVideoTrack = true;
                        }
                    }

                    if (pub.videoTrack instanceof LocalVideoTrack) {
                        const stats = await pub.videoTrack?.getSenderStats();
                        setStatsLocal(stats);
                    }
                });
                setCurrentBitrate(total);
                setConnectionQuality(participant.connectionQuality);

                // очищаем videoSize если нет активного видео трека
                if (!hasActiveVideoTrack) {
                    setVideoSize(undefined);
                }
            } else {
                const track = entity;
                const bitrate = track.currentBitrate;
                setCurrentBitrate(bitrate);

                if (track instanceof LocalVideoTrack) {
                    track.getSenderStats().then((stats) => {
                        setStatsLocal(stats);
                    });
                }
            }
        }, 1000);

        if (!isStatisticVisible) clearInterval(interval);

        return () => {
            clearInterval(interval);
        };
    }, [entity, isStatisticVisible, type]);

    return {
        connectionQuality,
        currentBitrate,
        stats,
        videoSize,
        onVideoSizeChanged,
    };
};
